/**
 * Dexie schema v1 for tournament + hand history + resume snapshots.
 * Spec: FIRST_INSTRUCTION.md §4.8 L304.
 *
 * Three tables:
 *  - `tournaments` — one row per tournament. Indexed by start/end time.
 *  - `hands` — one row per hand. Composite [tournamentId+handNumber] index
 *    powers `listHands(tournamentId)` ordered by hand number.
 *  - `resumeSnapshots` — one row per in-progress tournament; cleared when
 *    the tournament finishes. Lets the user resume after a refresh / disconnect.
 */
import Dexie, { type Table } from 'dexie';
import type { Action, Event, HandState, TournamentState } from '@/engine/types';

export const SCHEMA_VERSION = 1;
export const RESUME_SCHEMA_VERSION = 1;
export const DEFAULT_DB_NAME = 'poker-with-ai-agents';

export type TournamentMeta = {
  playerIds: ReadonlyArray<string>;
  characterCodenames?: ReadonlyArray<string>;
  /** Provider used for AI agents during this tournament. */
  agentMode?: 'offline' | 'llm';
  llmProvider?: 'openai' | 'anthropic' | 'google';
  /** Free-form annotations for review UI. */
  notes?: string;
};

export type TournamentRow = {
  id: string;
  startedAt: number;
  /** Null while the tournament is in progress. */
  endedAt: number | null;
  /** Final placement of the human player (1 = winner). Null until finished. */
  resultRank: number | null;
  /** Prize pool in chips at tournament end (or current pool while running). */
  prizePoints: number;
  meta: TournamentMeta;
};

export type HandPayout = {
  playerId: string;
  potIndex: number;
  amount: number;
  handRank?: string;
  handDescription?: string;
};

export type HandResultRow = {
  payouts: ReadonlyArray<HandPayout>;
  boardRevealedTo: 'preflop' | 'flop' | 'turn' | 'river' | 'showdown' | 'payout';
};

export type HandRow = {
  /** `${tournamentId}:${handNumber}` — stable + derivable. */
  id: string;
  tournamentId: string;
  handNumber: number;
  /** Per-hand RNG seed used to derive deck shuffle. */
  seed: string;
  /** Blind level numeric (1..15). */
  level: number;
  /** Canonical PokerStars hand history text. */
  pokerstarsHH: string;
  /** Full action sequence in engine semantics. JSON-clonable. */
  actions: ReadonlyArray<Action>;
  result: HandResultRow;
  /** Wall-clock at which the hand finished (ms). */
  playedAt: number;
};

/**
 * Mid-tournament snapshot used by the resume layer. Single row per tournament
 * (PK = tournamentId). On hand end, `handState` is null; on action mid-hand,
 * it carries the full HandState so reload picks up at the exact pendingActor.
 */
export type ResumeSnapshot = {
  tournamentId: string;
  schemaVersion: number;
  tournamentState: TournamentState;
  handState: HandState | null;
  events: ReadonlyArray<Event>;
  updatedAt: number;
};

export class PokerDb extends Dexie {
  tournaments!: Table<TournamentRow, string>;
  hands!: Table<HandRow, string>;
  resumeSnapshots!: Table<ResumeSnapshot, string>;

  constructor(name: string = DEFAULT_DB_NAME) {
    super(name);
    this.version(SCHEMA_VERSION).stores({
      // Indexes: PK + secondary indexes used by query helpers.
      tournaments: '&id, startedAt, endedAt',
      hands: '&id, tournamentId, [tournamentId+handNumber], handNumber',
      resumeSnapshots: '&tournamentId, updatedAt',
    });
  }
}

let singleton: PokerDb | null = null;

/** Lazily-initialized singleton for the production app. */
export function getDb(): PokerDb {
  if (!singleton) singleton = new PokerDb();
  return singleton;
}

/** Per-test factory — every Vitest case should use a unique name. */
export function createDb(name: string): PokerDb {
  return new PokerDb(name);
}

/** Close + drop the production singleton (test/maintenance only). */
export function closeAndResetDb(): void {
  if (singleton) {
    singleton.close();
    singleton = null;
  }
}
